import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";
import { getEvBvReportDetails } from "../../../api/postCall/getEvBvReport";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { fetchNycbsMedicalPaOrdersFromFirebase } from "../../../redux/slice/nycbsMedicalPaPatientsSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";

interface NycbsMedicalPaDetailsModalProps {
  onClose: () => void;
  id: string;
}

const NycbsMedicalPaDetailsModal = (props: NycbsMedicalPaDetailsModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { patients, loading } = useSelector(
    (state: RootState) => state.nycbsMedicalPaPatients,
  );
  const { data } = useSelector((state: RootState) => state.nycbsMedicalPa);
  const totalOrders = useMemo(() => {
    return data?.find((order) => order?.job_id === props.id)?.total_orders ?? 0;
  }, [data, props.id]);
  const jobCreatedAt = useMemo(() => {
    return data?.find((order) => order?.job_id === props.id)?.created_at ?? "";
  }, [data, props.id]);
  const lastReportGeneratedAt = useMemo(() => {
    return (
      data?.find((order) => order?.job_id === props.id)?.report_generated_at ??
      ""
    );
  }, [data, props.id]);
  const singleData = useMemo(() => {
    return data?.find((order) => order?.job_id === props.id);
  }, [data, props.id]);
  const [statusData, setStatusData] = useState({
    completedOrders: 0,
    inProgressOrders: 0,
    errorOrders: 0,
    pendingOrders: 0,
  });
  const shouldDisableSave = useMemo(() => {
    const isCompleted = singleData?.status === "completed";
    const maxRetryCount = singleData?.max_refresh ?? 0;
    const currentRetryCount = singleData?.number_of_refetch ?? 0;
    const createdAt = singleData?.created_at ?? "";
    const lastReportGeneratedAt = singleData?.report_generated_at ?? "";
    const isReportGeneratedInLast24Hours =
      moment(lastReportGeneratedAt).diff(moment(createdAt), "hours") <= 24;
    return (
      !isCompleted ||
      currentRetryCount === maxRetryCount ||
      !isReportGeneratedInLast24Hours
    );
  }, [singleData]);

  const refetchReportDetails = () => {
    const body = {
      org_id: "nycbs_medical_pa",
      job_id: props.id,
    };
    getEvBvReportDetails(body);
    toast.success(ToastMessages.RE_RUNNING_ELIGIBILITY_CHECK);
    props.onClose();
  };

  useEffect(() => {
    dispatch(fetchNycbsMedicalPaOrdersFromFirebase(props.id));
  }, [dispatch, props.id]);
  useEffect(() => {
    if (patients && patients.length > 0) {
      const completedOrders = patients.filter(
        (patient) => patient.status === "completed",
      ).length;
      const inProgressOrders = patients.filter(
        (patient) => patient.status === "in_progress",
      ).length;
      const errorOrders = patients.length - completedOrders - inProgressOrders;
      const pendingOrders = totalOrders - patients.length;
      setStatusData({
        completedOrders,
        inProgressOrders,
        errorOrders,
        pendingOrders,
      });
    }
  }, [patients]);
  return (
    <Modal
      dialogId={"nycbs-medical-pa-details"}
      onSave={refetchReportDetails}
      title={"Job Details"}
      saveButtonText={"Re-run"}
      cancelText={"Close"}
      onCancel={props.onClose}
      onClose={props.onClose}
      disableSave={shouldDisableSave}
    >
      <div className="nycbs-medical-pa-details--body flex flex-col gap-2">
        <div className="nycbs-medical-pa-details--details rounded-md bg-gray-100 p-2 text-tiny font-normal">
          Job Id : <span className="font-bold">{props.id}</span>
        </div>
        <div className="nycbs-medical-pa-details--details rounded-md bg-gray-100 p-2 text-tiny font-normal">
          Job Created At :{" "}
          <span className="font-bold">
            {jobCreatedAt
              ? moment(jobCreatedAt).format("MMM DD, YYYY hh:mm A")
              : "N/A"}
          </span>
        </div>
        <div className="nycbs-medical-pa-details--details rounded-md bg-gray-100 p-2 text-tiny font-normal">
          Last Report Generated At :{" "}
          <span className="font-bold">
            {lastReportGeneratedAt
              ? moment(lastReportGeneratedAt).format("MMM DD, YYYY hh:mm A")
              : "N/A"}
          </span>
        </div>
        <div className="nycbs-medical-pa-details--details rounded-md bg-gray-100 p-2 text-tiny font-normal">
          Total Orders : <span className="font-bold">{totalOrders}</span>
        </div>
        {loading ? (
          <>
            <div className="nycbs-medical-pa-details--more-info flex flex-wrap gap-2 gap-y-2 text-center text-body">
              Fetching orders status...
            </div>
          </>
        ) : (
          <>
            <div className="nycbs-medical-pa-details--more-info flex flex-wrap gap-2 gap-y-2">
              <div className="nycbs-medical-pa-details--info w-[48%]">
                <div className="nycbs-medical-pa-details--info-title rounded-md border border-[#005D49] bg-[#E6F3F0] p-2 text-tiny font-normal text-[#005D49]">
                  <div>Completed Orders</div>
                  <div className="text-body font-bold">
                    {statusData.completedOrders}
                  </div>
                </div>
              </div>
              <div className="nycbs-medical-pa-details--info w-[48%]">
                <div className="nycbs-medical-pa-details--info-title rounded-md border border-secondaryYellow-2 bg-secondaryYellow-11 p-2 text-tiny font-normal text-secondaryYellow-2">
                  <div>In Progress Orders</div>
                  <div className="text-body font-bold">
                    {statusData.inProgressOrders}
                  </div>
                </div>
              </div>
              <div className="nycbs-medical-pa-details--info w-[48%]">
                <div className="nycbs-medical-pa-details--info-title rounded-md border border-tertiaryRed-3 bg-tertiaryRed-11 p-2 text-tiny font-normal text-tertiaryRed-3">
                  <div>Error Orders</div>
                  <div className="text-body font-bold">
                    {statusData.errorOrders}
                  </div>
                </div>
              </div>
              <div className="nycbs-medical-pa-details--info w-[48%]">
                <div className="nycbs-medical-pa-details--info-title rounded-md border border-secondaryOrange-2 bg-secondaryOrange-12 p-2 text-tiny font-normal text-secondaryOrange-2">
                  <div>Pending Orders</div>
                  <div className="text-body font-bold">
                    {statusData.pendingOrders}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default NycbsMedicalPaDetailsModal;
